import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatSelectModule } from "@angular/material/select";
import { AsyncPipe, DatePipe, DecimalPipe, NgClass } from "@angular/common";
import { provideNativeDateAdapter } from "@angular/material/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { ReportService } from "../../../../../../../shared/src/lib/services/report.service";
import { ActivatedRoute, Router } from "@angular/router";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatTableModule } from "@angular/material/table";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { PlDetailComponent } from "./pl-detail/pl-detail.component";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { ApiService } from "../../../../../../../shared/src";
import { AlertComponent } from "../../../../components/alert/alert.component";
import { downloadJSONAsExcel, safeParseFloat } from "../../../../../../../shared/src/lib/utils/common";
import { TranslateModule } from "@ngx-translate/core";
import { CoreService } from "../../../../../../../shared/src/lib/services/core.service";
import { Observable, Subject } from "rxjs";
import { ReportUtilsService } from "../report-utils.service";
import { Medium24Pipe } from "../../../../../../../shared/src/lib/pipe/medium-24.pipe";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";
import { ReportLoaderComponent } from "../report-loader/report-loader.component";
import { BalanceService } from "../../../../../../../shared/src/lib/services/balance.service";
import { NgxPrintDirective } from "ngx-print";
import dayjs from "dayjs";
import { takeUntil } from "rxjs/operators";
import { getDateRangeFromReference, today } from "../../../../../../../shared/src/lib/utils/date-range";

@Component({
  selector: "app-betting-pl",
  animations: [
    trigger("detailExpand", [
      state("collapsed,void", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "*" })),
      transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
    ]),
  ],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatButtonToggleModule,
    DecimalPipe,
    MatProgressBarModule,
    MatTableModule,
    PlDetailComponent,
    NgClass,
    AlertComponent,
    TranslateModule,
    AsyncPipe,
    Medium24Pipe,
    ReportLoaderComponent,
    NgxPrintDirective,
    DatePipe,
  ],
  providers: [provideNativeDateAdapter(), DatePipe, DecimalPipe],
  templateUrl: "./betting-pl.component.html",
  styleUrls: ["./betting-pl.component.scss", "../report.scss"],
})
export class BettingPlComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  reportService = inject(ReportService);
  marketService = inject(MarketService);
  balanceService = inject(BalanceService);
  apiService = inject(ApiService);
  coreService = inject(CoreService);
  reportUtils = inject(ReportUtilsService);
  form: FormGroup;
  plData: any[] = [];
  backupData: any[] = [];
  aggregatePl: any[] = [];
  loader = inject(LoaderService);
  displayedColumns = ["market", "startTime", "settledDate", "pl"];
  expandedElements: any[] = [];
  balanceData: any = null;
  sport: any = new FormControl("Total P/L");
  $loading!: Observable<boolean>;
  marketCommissions: any = {};
  sportCommissions: any = {};
  totalCommission: number = 0;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
  ) {
    this.form = this.fb.group({
      WALLET_ID: [+this.route.snapshot.queryParams["WALLET_ID"] || ""],
      RANGE: [+this.route.snapshot.queryParams["RANGE"] || 3],
      FROM_DATE: [dayjs(this.route.snapshot.queryParams["FROM_DATE"]).format("YYYY-MM-DD")],
      TO_DATE: [dayjs(this.route.snapshot.queryParams["TO_DATE"]).format("YYYY-MM-DD")],
    });
  }

  get range() {
    return this.form.get("RANGE");
  }

  get fromDate() {
    return this.form.get("FROM_DATE");
  }

  get toDate() {
    return this.form.get("TO_DATE");
  }

  get walletId() {
    return this.form.get("WALLET_ID");
  }

  ngOnInit() {
    this.$loading = this.loader.isLoading("/v1/api/reports/balance_statement");

    this.sport?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.handleFilter(value);
    });

    this.toDate?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const customRange = this.rangeSelection({
        FROM_DATE: this.fromDate?.value,
        TO_DATE: value,
      });
      this.updateQueryParam({
        ...this.form.value,
        ...customRange,
        WALLET_ID: this.walletId?.value,
      });
    });

    this.range?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value !== 8) {
        const range = this.reportUtils.dateRange(value);
        this.form.patchValue(range);
      }
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: any) => {
      if (params["WALLET_ID"] && params["FROM_DATE"] && params["TO_DATE"]) {
        this.fetchBalanceStatement({
          RANGE: +params["RANGE"],
          WALLET_ID: +params["WALLET_ID"],
          FROM_DATE: params["FROM_DATE"],
          TO_DATE: params["TO_DATE"],
        });
      }
    });
    this.getBalance();
    this.subscribeToStatement();
  }

  resetDateRange() {
    this.form.patchValue({ FROM_DATE: "", TO_DATE: "" });
  }

  subscribeToStatement() {
    this.reportService.statement$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        // console.log(response);
        if (response?.result) {
          this.marketCommissions = this.getMarketCommissions(response?.result);
          this.sportCommissions = this.getSportCommission(response?.result);
          this.totalCommission = this.getTotalCommission(response?.result);
          // console.log(this.marketCommissions);
          const filteredResult = response?.result?.filter((item: any) => !["deposit", "transfer"].includes(item?.BET_ID) && item?.BET_ID > 0);
          // console.log(filteredResult);
          const groupedResult = this.groupByMarketId(filteredResult);
          // console.log(groupedResult);
          this.plData = groupedResult;
          this.backupData = groupedResult;
          this.aggregatePl = this.aggregateProfitLoss(groupedResult);
        }
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  onDateSelection() {
    this.range?.setValue(8);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  rangeSelection(form: any) {
    const { FROM_DATE, TO_DATE } = form || {};
    return FROM_DATE && TO_DATE
      ? { FROM_DATE: dayjs(FROM_DATE).format("DD-MMM-YYYY"), TO_DATE: dayjs(TO_DATE).format("DD-MMM-YYYY") }
      : { FROM_DATE: "", TO_DATE: "" };
  }

  updateQueryParam(values: any) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: values,
      queryParamsHandling: "merge",
    });
  }

  getBalance() {
    this.balanceService.balanceData$.pipe(takeUntil(this.destroy$)).subscribe((balance: any) => {
      if (balance) {
        this.form.patchValue({
          WALLET_ID: balance?.ID || "",
          RANGE: this.range?.value,
        });
        this.balanceData = balance;
      }
    });
  }

  handleFilter(sport: string) {
    if (sport === "Total P/L") {
      this.plData = this.backupData;
      return;
    }

    const isFancy = sport.includes("Fancy");
    const isBm = sport.includes("BM");

    this.plData = this.backupData.filter((x) => {
      const { PROVIDER, SPORT } = x || {};
      const isCricket = sport === "Cricket";

      if (isFancy) return PROVIDER === "FA";
      if (isBm) return PROVIDER === "BM";
      if (isCricket) return SPORT === sport && PROVIDER !== "FA" && PROVIDER !== "BM";

      return SPORT === sport;
    });
  }

  fetchBalanceStatement(params: any) {
    const { RANGE, ...rest } = params || {};
    this.reportService.fetchAccountStatement(rest);
  }

  getSportCommission(result: any[]) {
    return result.reduce((accumulator: any, current: any) => {
      const { SPORT: sportKey, PROVIDER: provider = "", DESCRIPTION, AMOUNT } = current || {};

      const isFancy = provider === "FA";
      const isBm = provider === "BM";
      const isCommission = DESCRIPTION === "commission";
      const amount = isCommission ? safeParseFloat(AMOUNT) : 0;

      // Determine the key for accumulation
      const key = sportKey === "Cricket" && (isFancy || isBm) ? (isFancy ? "Cricket Fancy" : "Cricket BM") : sportKey;

      // Initialize accumulator if it doesn't exist
      if (!accumulator[key]) {
        accumulator[key] = { SPORT: key, totalCommission: 0 };
      }

      // Add to total commission
      accumulator[key].totalCommission += amount;

      return accumulator;
    }, {});
  }

  getMarketCommissions(result: any[]) {
    return result.reduce((accumulator: any, current: any) => {
      const key = current.MARKET_ID;
      if (!accumulator[key]) {
        accumulator[key] = {
          MARKET_ID: key,
          totalCommission: 0,
        };
      }
      accumulator[key].totalCommission += current?.DESCRIPTION === "commission" ? safeParseFloat(current.AMOUNT) : 0;

      return accumulator;
    }, {});
  }

  getTotalCommission(result: any[]) {
    return result.reduce((accumulator: any, current: any) => {
      accumulator += current?.DESCRIPTION === "commission" ? safeParseFloat(current.AMOUNT) : 0;
      return accumulator;
    }, 0);
  }

  groupByMarketId(result: any[]) {
    const groupedByMarketId = result
      .filter((item) => !/commission/i.test(item.DESCRIPTION))
      .reduce((accumulator: any, current: any) => {
        const key = current.MARKET_ID;

        if (!accumulator[key]) {
          accumulator[key] = {
            ...current,
            MARKET_ID: key,
            PROFIT: 0,
            COMMISSION: this.marketCommissions[key]?.totalCommission,
            details: [],
          };
        }

        accumulator[key].PROFIT += safeParseFloat(current.PROFIT);
        accumulator[key].details.push(current);

        return accumulator;
      }, {});

    return Object.values(groupedByMarketId);
  }

  aggregateProfitLoss(data: any[]) {
    // console.log(data);
    const aggregatePl = data.reduce((acc: any, item: any) => {
      const { SPORT, PROVIDER = "", PROFIT } = item || {};
      const profit = safeParseFloat(PROFIT);

      // Determine key for aggregation
      const key = SPORT === "Cricket" && (PROVIDER === "FA" || PROVIDER === "BM") ? (PROVIDER === "FA" ? "Cricket Fancy" : "Cricket BM") : SPORT;

      // Aggregate profit
      acc[key] = (acc[key] || 0) + profit;

      return acc;
    }, {});

    const aggregatedPlArray = Object.keys(aggregatePl).map((sport) => ({
      sport: sport,
      value: aggregatePl[sport] + this.sportCommissions[sport]?.totalCommission,
    }));

    const totalPl = aggregatedPlArray.reduce((sum, item) => sum + safeParseFloat(item.value), 0);

    aggregatedPlArray.push({ sport: "Total P/L", value: totalPl });

    return aggregatedPlArray;
  }

  handleExpansion(element: any, event: Event) {
    event.stopPropagation();

    if (!element?.MARKET_ID) return;

    const item = JSON.stringify(element);
    if (this.isExpanded(element)) {
      this.expandedElements = this.expandedElements.filter((x: any) => x !== item);
    } else {
      this.expandedElements = [...this.expandedElements, item];
    }
  }

  get gamingPl() {
    return this.backupData.reduce((acc, item) => acc + safeParseFloat(item.PROFIT), 0);
  }

  isExpanded(item: any) {
    return this.expandedElements.includes(JSON.stringify(item));
  }

  prepareDataForExcel(data: any[]) {
    const dataForExcel: any[] = data.map((each) => ({
      Market: `${each?.EVENT_NAME || "-"}:${each?.MARKET_NAME || "-"}`,
      "Start Time": this.datePipe.transform(each?.START_TIME, "M/d/yyyy H:mm"),
      Settled: this.datePipe.transform(each?.SETTLED_ON, "M/d/yyyy H:mm"),
      "Profit/Loss": this.decimalPipe.transform(each?.PROFIT + each?.COMMISSION, "1.2-2"),
    }));

    this.aggregatePl.forEach((item) => {
      dataForExcel.push({
        Settled: item.sport,
        "Profit/Loss": safeParseFloat(item.value),
      });
    });

    dataForExcel.push({
      Settled: "Gaming P&L",
      "Profit/Loss": this.gamingPl,
    });

    dataForExcel.push({
      Settled: "Total Commission",
      "Profit/Loss": this.totalCommission,
    });

    return dataForExcel;
  }

  handleDownload() {
    const excelData = this.prepareDataForExcel(this.backupData);
    downloadJSONAsExcel(excelData, "betting profit & loss report");
  }

  protected readonly getDateRangeFromReference = getDateRangeFromReference;
  protected readonly today = today;
}
